import {
  createMiniOrbiLoaderEntranceFrames,
  createMiniOrbiLoaderBlinkFrames,
  createMiniOrbiLoaderExitFrames,
  createMiniOrbiLoaderLoopFrames,
  createMiniOrbiLoaderMainFrames,
  MINI_ORBI_LOADER_COMPLETE_SIGNAL,
  MINI_ORBI_LOADER_DURATION,
  MINI_ORBI_LOADER_INDICES,
} from "../../core/mini-orbi-loader-keyframes.js";

export { MINI_ORBI_LOADER_COMPLETE_SIGNAL };

export const meta = Object.freeze({
  id: "mini-orbi-loader-large",
  label: "Loader · 3 Mini-Orbis",
  duration: MINI_ORBI_LOADER_DURATION,
  managesOwnLoop: true,
  description: "Drei kleine Mini-Orbis wechseln auf phasenversetzten Jonglierbahnen gleichmäßig ihre Positionen.",
  tags: ["loader", "loading", "mini-orbi", "swarm", "bounce", "signal", "chat", "loop"],
});

const awaitAnimations = (animations) => Promise.allSettled(animations.map(({ finished }) => finished));

export async function run({ elements, scene, animate, signal, reducedMotion, waitForSignal }) {
  if (!scene?.miniSwarm || scene.miniBodies.length !== 7 || scene.miniShadows.length !== 7) {
    throw new Error("Der Mini-Orbi-Loader benötigt den vollständigen Mini-Orbi-Pool in der Stage.");
  }
  if (typeof waitForSignal !== "function") {
    throw new Error("Der Mini-Orbi-Loader benötigt die externe Controller-Signal-Schnittstelle.");
  }
  const miniBlinks = MINI_ORBI_LOADER_INDICES.map((miniIndex) =>
    scene.miniBodies[miniIndex].querySelector(".orbi__blink"));
  if (miniBlinks.some((blink) => !blink)) {
    throw new Error("Der Mini-Orbi-Loader benötigt für jeden Mini-Orbi einen Blink-Kanal.");
  }

  let completionRequested = false;
  const completionPromise = waitForSignal(MINI_ORBI_LOADER_COMPLETE_SIGNAL).then((result) => {
    if (!result.aborted) completionRequested = true;
    return result;
  });

  const main = createMiniOrbiLoaderMainFrames();
  const entranceOptions = { duration: 480, easing: "linear" };
  const entranceAnimations = [
    animate(elements.actor, main.hideActor, entranceOptions),
    animate(elements.shadow, main.hideShadow, entranceOptions),
  ];

  MINI_ORBI_LOADER_INDICES.forEach((miniIndex, slotIndex) => {
    const frames = createMiniOrbiLoaderEntranceFrames(slotIndex);
    entranceAnimations.push(
      animate(scene.miniBodies[miniIndex], frames.body, entranceOptions),
      animate(scene.miniShadows[miniIndex], frames.shadow, entranceOptions),
    );
  });
  await awaitAnimations(entranceAnimations);

  if (reducedMotion) {
    await completionPromise;
  } else {
    let cycleIndex = 0;
    while (!completionRequested && !signal?.aborted) {
      const frames = createMiniOrbiLoaderLoopFrames(cycleIndex);
      const loopOptions = {
        duration: MINI_ORBI_LOADER_DURATION,
        easing: "linear",
        transient: true,
      };
      const animations = [];
      MINI_ORBI_LOADER_INDICES.forEach((miniIndex, slotIndex) => {
        animations.push(
          animate(scene.miniBodies[miniIndex], frames[slotIndex].body, loopOptions),
          animate(scene.miniShadows[miniIndex], frames[slotIndex].shadow, loopOptions),
          animate(miniBlinks[slotIndex], createMiniOrbiLoaderBlinkFrames(slotIndex, cycleIndex), loopOptions),
        );
      });
      await awaitAnimations(animations);
      cycleIndex += 1;
    }
    await completionPromise;
  }

  if (signal?.aborted) return;

  const exitOptions = { duration: 360, easing: "linear" };
  const exitAnimations = [
    animate(elements.actor, main.showActor, exitOptions),
    animate(elements.shadow, main.showShadow, exitOptions),
  ];
  MINI_ORBI_LOADER_INDICES.forEach((miniIndex, slotIndex) => {
    const frames = createMiniOrbiLoaderExitFrames(slotIndex);
    exitAnimations.push(
      animate(scene.miniBodies[miniIndex], frames.body, exitOptions),
      animate(scene.miniShadows[miniIndex], frames.shadow, exitOptions),
    );
  });
  await awaitAnimations(exitAnimations);
}
